import moment from "moment";
import { LoaderFunctionArgs, redirect, useLoaderData, useRevalidator } from "react-router-dom";
import { ShowRow } from "../../components/ShowRow";
import { ShowLayout } from "../../layout/ShowLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { Order } from "../../types/order";
import { RefundOptions } from "../../types/refund";
import { capitalize, usdFormat } from "../../utils";
import { RefundModal } from "./components/RefundModal";
import { LineItemRefundModal, RefundMode, processorLabel, processorOf } from "./components/LineItemRefundModal";
import { RefundHistoryTable } from "./components/RefundHistoryTable";
import { GET_ORDER, GET_REFUND_OPTIONS } from "./queries";
import { TotalList } from "./components/Total";
import { ItemsTable } from "./components/ItemsTable";
import { DiscountTable } from "./components/DiscountTable";
import { TransactionTable } from "./components/TransactionTable";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { generateReceipt } from "./actions/generate-new-receipt";
import ManageTabModal from "./components/ManageTabModal";
import { useCallback, useEffect, useRef, useState } from "react";

const client = createApolloClient();

// Report pages link an order as /orders/<reference>/<id>. For a refund leg the
// reference in the link is the leg's own, not the order's (RP-2563), so the
// order is resolved by id alone (orders.id is unique on its own) and the browser
// is sent to the canonical URL when the reference in the link does not match.
export const orderShowLoader = async ({ params }: LoaderFunctionArgs) => {
  const id = Number(params.id);
  if (!Number.isInteger(id)) {
    throw new Response("Order not found", { status: 404 });
  }

  const { data } = await client.query({
    query: GET_ORDER,
    variables: { id },
  });
  const order = data.orders?.[0];
  if (!order) {
    throw new Response(`Order ${id} not found`, { status: 404 });
  }
  if (params.reference_id !== order.reference_id) {
    return redirect(`/orders/${encodeURIComponent(order.reference_id)}/${order.id}`);
  }
  return order;
};

// Reads getRefundOptions. null while loading; `legacy` turns true when the
// action is not deployed, so the page falls back to the old refund action.
const useRefundOptions = (orderId: number) => {
  const [options, setOptions] = useState<RefundOptions | null>(null);
  const [legacy, setLegacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.query<{ getRefundOptions: RefundOptions }>({
        query: GET_REFUND_OPTIONS,
        variables: { order_id: orderId },
        fetchPolicy: "network-only",
      });
      setOptions(data.getRefundOptions);
      setLegacy(false);
    } catch (error) {
      console.warn("getRefundOptions is not available, using the old refund action", error);
      setOptions(null);
      setLegacy(true);
    }
    setLoading(false);
  }, [orderId]);
  useEffect(() => {
    load();
  }, [load]);
  return { options, legacy, loading, reload: load };
};

export const OrderShow = () => {
  const [generating, setGenerating] = useState(false);
  const [refundMode, setRefundMode] = useState<RefundMode | null>(null);
  const [manageTabOpen, setManageTabOpen] = useState(false);
  const refundButton = useRef<HTMLButtonElement>(null);
  const order = useLoaderData() as Order;
  const revalidator = useRevalidator();
  const { options, legacy, reload } = useRefundOptions(order.id);

  // With the action: the server says what the processor allows.
  // Without it: Void is hidden on Stripe (captures at once), the rest is offered.
  const canVoid = options ? options.available_actions.includes("void") : legacy && processorOf(order) !== "stripe";
  const canFull = options ? options.available_actions.includes("full") : legacy;
  const canPartial = options ? options.available_actions.includes("line_item") : legacy;
  const notVoided = order.status !== "voided";

  const statusLabel = (() => {
    if (order.status === "refund") {
      return options && options.remaining_amount > 0 ? "Partially refunded" : "Refunded";
    }
    return capitalize(order.status);
  })();

  const onRefundDone = () => {
    revalidator.revalidate();
    reload();
  };

  return (
    <>
      <ShowLayout
        header={`Order #${order.id}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {notVoided && canVoid && (
              <PrimaryButton
                onClick={() => {
                  if (options) setRefundMode("void");
                  else refundButton.current?.click();
                }}
              >
                Void
              </PrimaryButton>
            )}
            {/* One Refund button (Alex, 2026-10-13). The modal picks lines; its "Full refund"
                button selects everything. Without line items (split tender) it opens locked to the full amount. */}
            {notVoided && (canFull || canPartial) && (
              <PrimaryButton onClick={() => setRefundMode(canPartial ? "partial" : "full")}>Refund</PrimaryButton>
            )}
            <SecondaryButton
              isLoading={generating}
              onClick={async () => {
                setGenerating(true);
                await generateReceipt(order.reference_id);
                setGenerating(false);
              }}
            >
              Generate Receipt
            </SecondaryButton>
            {order.is_tab && (
              <button
                type="button"
                className="inline-flex justify-center items-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                style={{ lineHeight: 2 }}
                onClick={() => setManageTabOpen(true)}
              >
                Manage Tab
              </button>
            )}
          </div>
        }
      >
        {order.transaction_at && (
          <ShowRow label="Tablet Transaction Time" value={moment(order.transaction_at).format("lll")} />
        )}
        <ShowRow
          label="Server Transaction Time"
          value={order.created_at ? moment(order.created_at).format("lll") : "N/A"}
        />
        <ShowRow label="Last Updated Time" value={order.updated_at ? moment(order.updated_at).format("lll") : "N/A"} />
        <ShowRow label="Phone Number" value={order.phone_number} />
        <ShowRow label="Order Status" value={statusLabel} />
        {/* Only when a refund is on record (Alex, 2026-10-13): what came back, through which processor. */}
        {options &&
          (options.refunded_amount > 0 || options.refunds.length > 0 || order.status === "refund" || order.status === "voided") && (
            <ShowRow
              label="Refund"
              value={`${processorLabel(options.processor)}${options.tender ? ` · ${options.tender}` : ""}${
                options.settled === true ? " · settled" : options.settled === false ? " · not settled" : ""
              } · refunded ${usdFormat.format(options.refunded_amount / 100)} of ${usdFormat.format(options.approved_amount / 100)}${
                options.remaining_amount > 0 ? `, ${usdFormat.format(options.remaining_amount / 100)} remaining` : ""
              }`}
            />
          )}
        <ShowRow label="Reference ID" value={order.reference_id} />
        <ShowRow label="MX Ref ID" value={order.mx_ref_id} />
        <ShowRow label="Event" value={order.event?.name} />
        <ShowRow label="Location" value={order.location?.name} />
        <ShowRow label="Vendor" value={order.vendor?.name} />
        <ShowRow label="Menu" value={order.menu?.name} />
        <ShowRow label="UID" value={order.uid} />

        <ShowRow label="Clerk" value={order.user?.username} />
        <ShowRow label="Build ID" value={order.new_order_payload ? order.build_id : order.device_id} />
        <ShowRow label="Device Android ID" value={order.new_order_payload ? order.android_device_id : order.device_app_id} />
        <ShowRow label="Order Notes" value={order.notes} />
      </ShowLayout>
      <div className="mt-2">
        <ShowLayout header="Items">
          <ItemsTable items={order.items} />
        </ShowLayout>
      </div>
      {order.discount && (
        <div className="mt-2">
          <ShowLayout header="Discounts">
            <DiscountTable />
          </ShowLayout>
        </div>
      )}
      <div className="mt-2">
        <ShowLayout header="Transactions">
          <TransactionTable />
        </ShowLayout>
      </div>
      {options && options.refunds.length > 0 && (
        <div className="mt-2">
          <ShowLayout header="Refunds">
            <RefundHistoryTable refunds={options.refunds} />
          </ShowLayout>
        </div>
      )}
      <div className="mt-2">
        <TotalList />
      </div>
      {/* Old void modal: used only while the new action is not deployed. */}
      <RefundModal partial={false} onClose={onRefundDone}>
        {({ setOpen }) => <button ref={refundButton} onClick={() => setOpen(true)} />}
      </RefundModal>
      <LineItemRefundModal
        isOpen={refundMode !== null}
        mode={refundMode ?? "partial"}
        options={options}
        onClose={() => setRefundMode(null)}
        onDone={onRefundDone}
      />
      {order.is_tab && <ManageTabModal isOpen={manageTabOpen} onClose={() => setManageTabOpen(false)} order={order} />}
    </>
  );
};
